import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { API } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import { useAuth } from "@/context/AuthContext";
import { greeting, initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Mic, Pause, Play, Square, Loader2, Sparkles, UserPlus, Search, ImageIcon,
  FolderKanban, ArrowRight, Check, AlertTriangle, ChevronLeft, Pencil, MicOff,
  Keyboard, QrCode, Upload, Smartphone, FileUp, Copy, Circle, X, Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ImagingReview } from "@/components/common/ImagingReview";
import { VoiceCheck, VoiceSteps, TopicList } from "@/components/voice/VoiceCheck";
import { topicsFor as voiceTopicsFor, wizardFromVoice } from "@/components/voice/topics";
import { toast } from "sonner";

const STATUS_HINT = { Missing: "Missing", "Root stump": "Root stump", Mobile: "Mobile", "Grossly carious": "Grossly carious" };

const CONDITION_TOPICS = {
  implant: [
    { key: "name", label: "Patient name", has: (c) => !!c.patient_name && c.patient_name !== "New Patient" },
    { key: "cc", label: "Chief complaint", has: (c) => !!c.chief_complaint },
    { key: "mh", label: "Medical history", has: (c) => (c.medical_history || []).length > 0 },
    { key: "dh", label: "Dental history", has: (c) => (c.dental_history || []).length > 0 },
    { key: "habits", label: "Smoking / diabetes / habits", has: (c) => (c.risk_factors || []).length > 0 },
    { key: "soft", label: "Soft tissue phenotype", has: (c) => !!c.soft_tissue },
    { key: "teeth", label: "Tooth findings", has: (c) => (c.tooth_findings || []).length > 0 },
    { key: "arch", label: "Edentulous / arch status", has: (c) => !!c.arch_status },
    { key: "sites", label: "Implant site measurements", has: (c) => (c.sites || []).length > 0 },
    { key: "expect", label: "Patient expectations", has: (c) => !!c.patient_expectations },
    { key: "imaging", label: "Imaging (OPG / CBCT)", has: (c, imgs) => (imgs || []).length > 0 },
  ],
  _default: [
    { key: "name", label: "Patient name", has: (c) => !!c.patient_name && c.patient_name !== "New Patient" },
    { key: "cc", label: "Chief complaint", has: (c) => !!c.chief_complaint },
    { key: "mh", label: "Medical history", has: (c) => (c.medical_history || []).length > 0 },
    { key: "dh", label: "Dental history", has: (c) => (c.dental_history || []).length > 0 },
    { key: "teeth", label: "Clinical findings", has: (c) => (c.tooth_findings || []).length > 0 },
    { key: "expect", label: "Patient expectations", has: (c) => !!c.patient_expectations },
    { key: "imaging", label: "Imaging / investigations", has: (c, imgs) => (imgs || []).length > 0 },
  ],
};
const topicsFor = (slug) => CONDITION_TOPICS[slug] || CONDITION_TOPICS._default;

function transcriptHas(c, label) {
  const t = (c?.transcript || "").toLowerCase();
  if (!t) return false;
  const words = label.toLowerCase().replace(/[^a-z0-9 ]/g, "").split(" ").filter((w) => w.length > 3);
  return words.some((w) => t.includes(w));
}
function topicsFromForm(form) {
  if (!form || !(form.questions || []).length) return null;
  const qs = (form.questions || []).filter((q) => q.voice_capturable !== false);
  if (!qs.length) return null;
  const bySection = {};
  qs.forEach((q) => { const s = q.section || "General"; (bySection[s] = bySection[s] || []).push(q); });
  const picked = [];
  Object.values(bySection).forEach((arr) => picked.push(arr[0]));
  qs.forEach((q) => { if (picked.length < 14 && !picked.includes(q)) picked.push(q); });
  return picked.slice(0, 14).map((q) => ({ key: q.id, label: q.label, has: (c) => transcriptHas(c, q.label) || (c?.form_answers?.[q.id] != null && c.form_answers[q.id] !== "") }));
}

function ChecklistPanel({ topics, c, images, live }) {
  const done = topics.filter((t) => c && t.has(c, images)).length;
  const pct = topics.length ? Math.round((done / topics.length) * 100) : 0;
  return (
    <div data-testid="copilot-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#00A85A]">Clinical copilot</span>
        {!live && <span className="text-xs font-semibold text-[#0B1512] dark:text-white">{done} / {topics.length} captured · {pct}%</span>}
      </div>
      {!live && <div className="mb-3 h-1.5 rounded-full bg-black/5 dark:bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-[#00A85A] transition-all duration-500" style={{ width: `${pct}%` }} /></div>}
      <ul className="grid grid-cols-1 gap-1.5 text-left sm:grid-cols-2">
        {topics.map((t) => {
          const ok = !live && c && t.has(c, images);
          return (
            <li key={t.key} className="flex items-center gap-2 text-xs">
              {ok ? <Check className="h-3.5 w-3.5 shrink-0 text-[#00A85A]" /> : <Circle className="h-3 w-3 shrink-0 text-[#8B9698]" />}
              <span className={ok ? "text-[#0B1512] dark:text-white" : "text-[#8B9698]"}>{t.label}</span>
            </li>
          );
        })}
      </ul>
      {live && <p className="mt-2 text-[10px] text-[#8B9698]">Cover these naturally — CONTAHA marks them off after you submit.</p>}
    </div>
  );
}

// ── Real-time audio waveform ────────────────────────────────────────────────
function Waveform({ getFrequency, active }) {
  const bars = useRef([]);
  useEffect(() => {
    if (!active) return;
    let raf;
    const loop = () => {
      const data = getFrequency?.();
      bars.current.forEach((el, i) => {
        if (!el) return;
        const v = data ? data[(i * 2) % data.length] / 255 : 0;
        el.style.transform = `scaleY(${(0.12 + v * 0.88).toFixed(3)})`;
      });
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [active, getFrequency]);
  return (
    <div className="flex h-10 items-center justify-center gap-[3px]" aria-hidden>
      {Array.from({ length: 40 }).map((_, i) => (
        <span
          key={i}
          ref={(el) => (bars.current[i] = el)}
          className="w-[3px] h-8 origin-center rounded-full bg-[#00A85A] transition-transform duration-75"
          style={{ transform: "scaleY(0.12)" }}
        />
      ))}
    </div>
  );
}

// ── Structured clinical card ────────────────────────────────────────────────
function StructCard({ label, children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.4, ease: "easeOut" }}
      className="rounded-2xl border border-black/5 bg-white/80 p-4 backdrop-blur-sm dark:border-white/10 dark:bg-white/[0.04]"
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#00A85A] mb-2">{label}</div>
      {children}
    </motion.div>
  );
}

function mergeByKey(existing = [], incoming = [], key) {
  if (!incoming || !incoming.length) return existing || [];
  const map = {};
  (existing || []).forEach((e) => { map[String(e[key])] = e; });
  incoming.forEach((n) => { if (n[key] != null) map[String(n[key])] = { ...map[String(n[key])], ...n }; });
  return Object.values(map);
}

export default function NewConsultation() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const preset = params.get("patient");
  const rec = useVoiceRecorder();

  const [phase, setPhase] = useState("entry"); // entry | listening | processing | check | upload | mic_error
  const [skipped, setSkipped] = useState({});
  const [patientCtx, setPatientCtx] = useState(null); // null=new, or {id,name,...}
  const [c, setC] = useState(null); // active consultation
  const [structuring, setStructuring] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [today, setToday] = useState(null);

  // patient modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState("choose"); // choose | search | context
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [candidate, setCandidate] = useState(null);
  const [dup, setDup] = useState(null); // possible duplicate patient
  const [formMode, setFormMode] = useState(false);
  const [entryStep, setEntryStep] = useState("mode"); // mode | condition
  const [tier] = useState("comprehensive");
  const [images, setImages] = useState([]);
  const [uploadSession, setUploadSession] = useState(null);
  const fileInputRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("contaha_token") : "";
  const [conditions, setConditions] = useState([]);
  const [condition, setCondition] = useState(null);
  const [effectiveForm, setEffectiveForm] = useState(null);
  const [manualAnswers, setManualAnswers] = useState({});
  const tierForm = tier === "quick" && effectiveForm ? { ...effectiveForm, questions: (effectiveForm.questions || []).filter((q) => q.tier !== "comprehensive") } : effectiveForm;
  const manualFields = (tierForm?.questions || []).filter((q) => q.voice_capturable === false);
  const setManual = (qid, v) => setManualAnswers((a) => { const next = { ...a, [qid]: v }; if (c?.id) persist({ form_answers: next, form_id: effectiveForm?.id }); return next; });

  useEffect(() => { api.get("/dashboard").then((r) => setToday(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/conditions").then((r) => { setConditions(r.data); setCondition((prev) => prev || r.data.find((x) => x.is_default) || r.data[0] || null); }).catch(() => {}); }, []);
  useEffect(() => { if (!condition?.slug) return; api.get(`/forms/effective?condition=${condition.slug}`).then((r) => setEffectiveForm(r.data)).catch(() => setEffectiveForm(null)); }, [condition?.slug]);

  // preset patient from URL (?patient=)
  useEffect(() => {
    if (!preset) return;
    api.get(`/patients/${preset}`).then((r) => setPatientCtx(r.data.patient)).catch(() => {});
  }, [preset]);

  // existing-patient search
  useEffect(() => {
    if (modalStep !== "search") return;
    const t = setTimeout(() => {
      if (!q.trim()) return setResults([]);
      const s = q.toLowerCase();
      api.get("/patients").then((r) => setResults(r.data.filter((p) =>
        p.name.toLowerCase().includes(s) || (p.patient_id || "").toLowerCase().includes(s) ||
        (p.phone || "").includes(q) || (p.email || "").toLowerCase().includes(s)
      ).slice(0, 8)));
    }, 200);
    return () => clearTimeout(t);
  }, [q, modalStep]);

  const openModal = () => { setModalStep("choose"); setQ(""); setResults([]); setCandidate(null); setModalOpen(true); };

  const loadImages = async (cid) => { const id = cid || c?.id; if (!id) return; try { const { data } = await api.get(`/imaging?consultation_id=${id}`); setImages(data); } catch (e) { console.warn("Couldn't load imaging", e); } };
  const uploadFiles = async (files) => {
    const list = Array.from(files || []);
    for (const f of list) {
      try { const fd = new FormData(); fd.append("file", f); fd.append("consultation_id", c.id); fd.append("image_type", "Other"); await api.post("/imaging/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch { toast.error(`Couldn't upload ${f.name}`); }
    }
    toast.success("Imaging added to the case"); loadImages();
  };
  const startPhoneUpload = async () => {
    try { const { data } = await api.post(`/consultations/${c.id}/upload-session`); setUploadSession(data); }
    catch { toast.error("Couldn't create the upload link"); }
  };
  useEffect(() => {
    if (phase !== "upload" || !c?.id) return;
    loadImages(c.id);
    const iv = setInterval(() => loadImages(c.id), 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [phase, c?.id]);

  // ── create consultation + start recording ──────────────────────────────────
  const beginConsultation = async (patient, cd) => {
    const cond = cd || condition;
    setPatientCtx(patient);
    setModalOpen(false);
    try {
      const { data } = await api.post("/consultations", {
        patient_id: patient?.id || null,
        patient_name: patient?.name || "New Patient",
        mode: formMode ? "form" : "voice", tier, status: "in_progress",
        condition: cond?.slug || null, condition_name: cond?.name || null,
        form_id: cond?.slug === condition?.slug ? effectiveForm?.id || null : null,
      });
      setC(data);
      if (formMode) { navigate(`/consultation/${data.id}/wizard`); return; }
      try {
        await rec.start();
        setPhase("listening");
      } catch (err) {
        setPhase("mic_error");
      }
    } catch {
      toast.error("Could not start the consultation. Please try again.");
    }
  };

  const persist = async (patch) => {
    setC((prev) => {
      const next = { ...prev, ...patch };
      api.put(`/consultations/${prev.id}`, next).catch(() => {});
      return next;
    });
  };

  // ── stop → transcribe → structure ───────────────────────────────────────────
  const handleStop = async () => {
    const result = await rec.stop(c?.id);
    if (result && result.error) { toast.error(result.error); setPhase(c?.transcript ? "check" : "entry"); return; }
    setPhase("processing");
    setStructuring(true);
    try {
      const text = typeof result === "string" ? result : "";
      const existing = { patient: { name: c.patient_name }, chief_complaint: c.chief_complaint, tooth_findings: c.tooth_findings, sites: c.sites, arch_status: c.arch_status, soft_tissue: c.soft_tissue, form_answers: c.form_answers, voice_exam: c.voice_exam };
      const { data } = await api.post("/ai/structure", { transcript: text, existing, condition: condition?.name, form_questions: tierForm?.questions || [] });
      const s = data.structured || {};
      const ve = c.voice_exam || {};
      const sx = s.exam || {};
      const patch = {
        voice_exam: { ...ve, medications: s.medications?.length ? s.medications : ve.medications, allergies: s.allergies?.length ? s.allergies : ve.allergies, smoking: s.smoking || ve.smoking,
          exam: { ...(ve.exam || {}), ...Object.fromEntries(Object.entries(sx).filter(([k, v]) => k !== "tmj" && v)) }, tmj: { ...(ve.tmj || {}), ...Object.fromEntries(Object.entries(sx.tmj || {}).filter(([, v]) => v !== null && v !== "")) } },
        transcript: (c.transcript ? c.transcript + " " : "") + text,
        form_answers: { ...(c.form_answers || {}), ...(s.form_answers || {}) },
        form_id: effectiveForm?.id || c.form_id,
        chief_complaint: s.chief_complaint || c.chief_complaint,
        medical_history: s.medical_history?.length ? s.medical_history : c.medical_history,
        dental_history: s.dental_history?.length ? s.dental_history : c.dental_history,
        arch_status: s.arch_status || c.arch_status,
        arch_side: s.arch_side || c.arch_side,
        soft_tissue: s.soft_tissue || c.soft_tissue,
        tooth_findings: mergeByKey(c.tooth_findings, s.tooth_findings, "fdi"),
        sites: mergeByKey(c.sites, s.sites, "site"),
        patient_expectations: s.patient_expectations || c.patient_expectations,
        patient_name: (c.patient_id ? c.patient_name : (s.patient?.name || c.patient_name)),
        extracted_patient: s.patient || c.extracted_patient,
      };
      await persist(patch);
      if (s.form_answers) setManualAnswers((a) => ({ ...a, ...s.form_answers }));
      if (c.patient_id && s.patient && (s.patient.age || s.patient.gender || s.patient.phone)) {
        api.put(`/patients/${c.patient_id}`, Object.fromEntries(Object.entries({ age: s.patient.age, gender: s.patient.gender, phone: s.patient.phone, email: s.patient.email }).filter(([, v]) => v))).catch(() => {});
      }
      setPhase("check");
    } catch (e) {
      toast.error(e.response?.data?.detail || "We couldn't analyse this recording. Record again or type the answers.");
      setPhase("check");
    } finally { setStructuring(false); }
  };

  const vTopics = voiceTopicsFor(condition?.slug || c?.condition, tierForm);
  const answer = (k, v) => setC((prev) => ({ ...prev, voice_answers: { ...(prev.voice_answers || {}), [k]: v } }));
  const recordAgain = async () => {
    if (c?.id) api.put(`/consultations/${c.id}`, c).catch(() => {});
    try { await rec.start(); setPhase("listening"); } catch { setPhase("mic_error"); }
  };
  const toUpload = async () => { await api.put(`/consultations/${c.id}`, c).catch(() => {}); setPhase("upload"); };
  const toReview = async () => {
    const slug = condition?.slug || c.condition || "implant";
    const w = wizardFromVoice(c, slug);
    const a = c.voice_answers || {};
    const formAnswers = { ...(c.form_answers || {}) };
    Object.entries(a).forEach(([k, v]) => { if (k.startsWith("form:") && v) formAnswers[k.slice(5)] = v; });
    try {
      await api.put(`/consultations/${c.id}`, { ...c, form_answers: formAnswers, patient_name: c.patient_id ? c.patient_name : (w.patient.name || c.patient_name), chief_complaint: a.complaint || c.chief_complaint, wizard: w });
      navigate(`/consultation/${c.id}/wizard`);
    } catch { toast.error("Couldn't open the review screen. Please try again."); }
  };

  const resolveDup = async (useExisting) => {
    if (useExisting) { await persist({ patient_id: dup.id, patient_name: dup.name }); }
    else {
      const ep = c.extracted_patient || {};
      const { data: p } = await api.post("/patients", { name: c.patient_name, age: ep.age, gender: ep.gender, phone: ep.phone, email: ep.email });
      await persist({ patient_id: p.id, patient_name: p.name });
    }
    setDup(null);
  };

  const retryMic = async () => {
    try { await rec.start(); setPhase("listening"); } catch { setPhase("mic_error"); }
  };

  const switchToForm = async () => { try { await rec.stop(); } catch (e) { console.warn("Recorder stop failed", e); } if (c?.id) navigate(`/consultation/${c.id}/wizard`); };
  const handleCancel = async () => { rec.abort(); if (c?.id) api.put(`/consultations/${c.id}`, { ...c, status: "cancelled" }).catch(() => {}); setPhase("entry"); setImages([]); setUploadSession(null); setC(null); };
  const setField = (patch) => setC((prev) => ({ ...prev, ...patch }));
  const removeTooth = (fdi) => persist({ tooth_findings: (c.tooth_findings || []).filter((x) => x.fdi !== fdi) });

  const generatePlan = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/treatment-plan", { consultation_id: c.id });
      toast.success("Treatment plan drafting started — doctor review required.");
      navigate(`/treatment-plan/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed."); setGenerating(false); }
  };

  const confirmTooth = (fdi) => persist({ tooth_findings: c.tooth_findings.map((x) => x.fdi === fdi ? { ...x, confidence: 100 } : x) });

  useEffect(() => { if (phase === "summary" && c?.form_answers) setManualAnswers((a) => ({ ...c.form_answers, ...a })); /* eslint-disable-next-line */ }, [phase]);
  const manualField = (q) => {
    const v = manualAnswers[q.id] ?? "";
    if (q.type === "yes_no") return (<div className="flex gap-2">{["Yes", "No"].map((o) => <button key={o} type="button" data-testid={`manual-${q.id}-${o}`} onClick={() => setManual(q.id, o)} className={cn("rounded-lg border px-4 py-1.5 text-sm font-semibold transition-colors", v === o ? "border-transparent bg-[#00A85A] text-white" : "border-black/10 bg-white/70 text-[#4E5F68] dark:border-white/10 dark:bg-white/5 dark:text-[#8B9698]")}>{o}</button>)}</div>);
    if (["single_choice", "dropdown"].includes(q.type)) return (<select data-testid={`manual-${q.id}`} value={v} onChange={(e) => setManual(q.id, e.target.value)} className="w-full rounded-lg border border-black/10 bg-white/80 px-3 py-2 text-sm text-[#0B1512] dark:border-white/10 dark:bg-white/5 dark:text-white"><option value="">Select</option>{(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</select>);
    return <Input data-testid={`manual-${q.id}`} value={v} onChange={(e) => setManual(q.id, e.target.value)} placeholder={q.type === "measurement" ? "mm" : ""} />;
  };

  const mm = String(Math.floor(rec.seconds / 60)).padStart(2, "0");
  const topics = topicsFromForm(tierForm) || topicsFor(condition?.slug);
  const ss = String(rec.seconds % 60).padStart(2, "0");

  const strip = today?.pipeline || {};
  const hasData = c && (c.tooth_findings?.length || c.sites?.length || c.chief_complaint || c.arch_status);

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-[#F4FAF6] dark:bg-[#111114]">
      {/* Atmospheric green glow */}
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(58% 46% at 62% 26%, rgba(0,168,90,0.20), rgba(0,168,90,0) 68%)" }} />
      <div className="pointer-events-none absolute inset-0 hidden dark:block" style={{ background: "radial-gradient(42% 34% at 26% 78%, rgba(0,168,90,0.10), rgba(0,168,90,0) 70%)" }} />

      <div className="relative mx-auto flex min-h-[calc(100vh-4rem)] max-w-3xl flex-col items-center justify-center px-5 py-12 text-center sm:px-8">
        <AnimatePresence mode="wait">
          {/* ── ENTRY ─────────────────────────────────────────────────────── */}
          {phase === "entry" && (
            <motion.div key="entry" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.4 }} className="w-full">
              <div className="text-sm font-medium text-[#4E5F68] dark:text-[#8B9698]">{greeting()}, {user?.name?.split(" ").slice(0, 2).join(" ") || "Doctor"}</div>
              <h1 className="mt-4 text-4xl font-extrabold leading-[1.05] tracking-tight text-[#0B1512] dark:text-white sm:text-5xl lg:text-[56px]">
                Consultation{" "}<span className="text-[#00A85A]">on the Go</span>
              </h1>
              <p className="mx-auto mt-5 max-w-md text-base text-[#4E5F68] dark:text-[#9BA7A5]">
                Choose how you'd like to work. CONTAHA structures the case, flags safety concerns, and builds one doctor-approved plan.
              </p>

              {entryStep === "mode" && (
                <div className="mx-auto mt-10 grid max-w-xl grid-cols-1 gap-4 sm:grid-cols-2" data-testid="entry-mode">
                  <button data-testid="choose-voice" onClick={() => { setFormMode(false); setEntryStep("condition"); }} className="group flex flex-col items-center gap-3 rounded-3xl border border-black/5 bg-white/70 p-8 backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:border-[#00A85A]/40 dark:border-white/10 dark:bg-white/[0.04]">
                    <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#00A85A] text-white shadow-lg"><Mic className="h-8 w-8" /></span>
                    <span className="text-lg font-bold text-[#0B1512] dark:text-white">Voice</span>
                    <span className="text-xs text-[#8B9698]">Speak naturally — CONTAHA transcribes and structures the case.</span>
                  </button>
                  <button data-testid="choose-form" onClick={() => { setFormMode(true); setEntryStep("condition"); }} className="group flex flex-col items-center gap-3 rounded-3xl border border-black/5 bg-white/70 p-8 backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:border-[#00A85A]/40 dark:border-white/10 dark:bg-white/[0.04]">
                    <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0B1512] text-white shadow-lg dark:bg-white dark:text-[#0B1512]"><Keyboard className="h-8 w-8" /></span>
                    <span className="text-lg font-bold text-[#0B1512] dark:text-white">Form</span>
                    <span className="text-xs text-[#8B9698]">Fill a guided, condition-specific form step by step.</span>
                  </button>
                </div>
              )}

              {entryStep === "condition" && (
                <div className="mx-auto mt-10 w-full max-w-2xl" data-testid="entry-condition">
                  <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="entry-form-mode">
                    <span className="text-base font-bold text-[#0B1512] dark:text-white">{formMode ? "Form consultation" : "Voice consultation"}</span>
                    <Button variant="outline" size="sm" onClick={() => setEntryStep("mode")} className="rounded-full border-[#00A85A]/50 text-base font-semibold text-[#00A85A] hover:bg-[#00A85A]/10" data-testid="entry-change-form"><ChevronLeft className="mr-1 h-4 w-4" />Change form</Button>
                  </div>
                  <div className="text-sm font-semibold text-[#4E5F68] dark:text-[#8B9698] mb-3">Choose the consultation type</div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {conditions.map((cd) => (
                      <button key={cd.id} data-testid={`condition-${cd.slug}`} onClick={() => { setCondition(cd); beginConsultation(patientCtx, cd); }}
                        className="group flex flex-col items-center gap-3 rounded-2xl border border-black/5 bg-white/70 p-6 backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:border-[#00A85A]/40 dark:border-white/10 dark:bg-white/[0.04]">
                        {cd.image_url ? <img src={cd.image_url.startsWith("http") ? cd.image_url : `${API}/files/${cd.image_url}?auth=${token}`} alt="" className="h-12 w-12 rounded-xl object-cover" /> : <span className="h-12 w-12 rounded-xl" style={{ background: cd.color }} />}
                        <span className="text-sm font-bold text-[#0B1512] dark:text-white">{cd.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {patientCtx && (
                <div className="mx-auto mt-6 inline-flex items-center gap-2.5 rounded-full border border-black/5 bg-white/70 py-1.5 pl-1.5 pr-4 backdrop-blur dark:border-white/10 dark:bg-white/5">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#00A85A] text-[10px] font-bold text-white">{initials(patientCtx.name)}</span>
                  <span className="text-xs font-semibold text-[#0B1512] dark:text-white">{patientCtx.name}</span>
                  <button onClick={() => setPatientCtx(null)} className="text-[11px] font-semibold text-[#8B9698] hover:text-[#00A85A]">change</button>
                </div>
              )}
            </motion.div>
          )}

          {/* ── LISTENING ─────────────────────────────────────────────────── */}
          {phase === "listening" && (
            <motion.div key="listening" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="w-full">
              <VoiceSteps at={0} />
              <div className="inline-flex items-center gap-2 rounded-full bg-red-500/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-500">
                <span className={cn("h-2 w-2 rounded-full bg-red-500", rec.state === "recording" && "animate-pulse")} />
                {rec.state === "paused" ? "Paused" : "Recording"}
              </div>
              <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-[#0B1512] dark:text-white sm:text-4xl">
                {rec.state === "paused" ? "Paused" : "I'm listening…"}
              </h2>
              <div className="mt-3 text-2xl font-bold tabular-nums text-[#00A85A]">{mm}:{ss}</div>
              <p className="mt-2 text-sm text-[#8B9698]" data-testid="voice-language-hint">Speak in English, Hindi or Hinglish — say the patient's name clearly.</p>
              {rec.state === "recording" && rec.quiet && <p className="mx-auto mt-2 max-w-md rounded-lg bg-amber-500/10 px-3 py-2 text-sm font-semibold text-amber-700" data-testid="voice-quiet-warning">We can't hear you — move closer to the microphone or check it isn't muted.</p>}

              <div className="mt-8 flex flex-col items-center">
                <div className="relative flex h-40 w-40 items-center justify-center rounded-full">
                  <span className={cn("absolute inset-0 rounded-full border-2 border-[#00A85A]/60", rec.state === "recording" && "animate-ping")} />
                  <span className="absolute inset-0 rounded-full bg-[#00A85A]/15" />
                  <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-[#00A85A] shadow-[0_14px_44px_-10px_rgba(0,168,90,0.6)]">
                    <Mic className="h-11 w-11 text-white" />
                  </div>
                </div>
                <div className="mt-6 w-full max-w-sm"><Waveform getFrequency={rec.getFrequency} active={rec.state === "recording"} /></div>
              </div>

              <div className="mt-8 flex items-center justify-center gap-3">
                {rec.state === "recording" ? (
                  <Button data-testid="pause-btn" variant="outline" onClick={rec.pause} className="h-11 rounded-full border-black/10 bg-white/70 px-5 dark:border-white/15 dark:bg-white/5 dark:text-white">
                    <Pause className="mr-1.5 h-4 w-4" />Pause
                  </Button>
                ) : (
                  <Button data-testid="resume-btn" onClick={rec.resume} className="h-11 rounded-full bg-[#00A85A] px-5 text-white hover:bg-[#008F4D]">
                    <Play className="mr-1.5 h-4 w-4" />Resume
                  </Button>
                )}
                <Button data-testid="stop-btn" onClick={handleStop} className="h-11 rounded-full bg-[#0B1512] px-6 text-white hover:bg-black dark:bg-white dark:text-[#0B1512] dark:hover:bg-white/90">
                  <Square className="mr-1.5 h-4 w-4" fill="currentColor" />Stop & Submit
                </Button>
                <Button data-testid="cancel-btn" variant="ghost" onClick={handleCancel} className="h-11 rounded-full px-4 text-[#8B9698] hover:text-red-500">Cancel</Button>
              </div>
              <p className="mt-6 text-sm text-[#8B9698]">Say things like “tooth 12 is missing, 34 is a root stump, soft tissue is thick”.</p>
              <button data-testid="switch-to-form" onClick={switchToForm} className="mx-auto mt-4 flex items-center gap-1.5 text-xs font-semibold text-[#8B9698] transition-colors hover:text-[#00A85A]"><Keyboard className="h-3.5 w-3.5" />Prefer to type? Switch to the form</button>
              <div className="mx-auto mt-6 max-w-xl rounded-2xl border border-black/5 bg-white/60 p-4 backdrop-blur dark:border-white/10 dark:bg-white/[0.04]">
                <div className="mb-2 text-left text-sm font-bold text-[#00A85A]">Cover these as you speak</div>
                <TopicList topics={vTopics} />
              </div>
            </motion.div>
          )}

          {/* ── PROCESSING ────────────────────────────────────────────────── */}
          {phase === "processing" && (
            <motion.div key="processing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="w-full">
              <VoiceSteps at={1} />
              <Loader2 className="mx-auto h-9 w-9 animate-spin text-[#00A85A]" />
              <h2 className="mt-5 text-2xl font-extrabold tracking-tight text-[#0B1512] dark:text-white sm:text-3xl">CONTAHA is structuring your case…</h2>
              <p className="mt-2 text-sm text-[#8B9698]">Converting your consultation into structured clinical data.</p>
              <div className="mx-auto mt-8 grid max-w-lg grid-cols-2 gap-3">
                {["Patient", "Clinical findings", "Soft tissue", "Site measurements"].map((l, i) => (
                  <div key={l} className="rounded-2xl border border-black/5 bg-white/60 p-4 dark:border-white/10 dark:bg-white/[0.03]">
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#00A85A] mb-2">{l}</div>
                    <motion.div className="h-3 rounded bg-[#00A85A]/15" animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.15 }} />
                    <motion.div className="mt-2 h-3 w-2/3 rounded bg-[#00A85A]/10" animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.15 + 0.2 }} />
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {phase === "check" && c && (
            <motion.div key="check" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full">
              <VoiceSteps at={1} />
              <VoiceCheck topics={vTopics} c={c} onAnswer={answer} onRecordAgain={recordAgain} onContinue={toUpload} skipped={skipped} onSkip={(k, v) => setSkipped((s) => ({ ...s, [k]: v }))} />
            </motion.div>
          )}

          {phase === "upload" && c && (
            <motion.div key="upload" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full" data-testid="voice-upload">
              <VoiceSteps at={2} />
              <h2 className="text-3xl font-extrabold tracking-tight text-[#0B1512] dark:text-white">Add X-rays, photos or reports</h2>
              <p className="mt-2 text-[#8B9698]">Optional. Upload from this computer or scan the QR code with your phone.</p>
              <div className="mx-auto mt-6 max-w-2xl rounded-2xl border border-black/5 bg-white/70 p-5 text-left dark:border-white/10 dark:bg-white/[0.04]">
                <div className="flex flex-wrap gap-2">
                  <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} data-testid="imaging-file-input" />
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="upload-from-computer" className="rounded-lg"><Upload className="mr-1.5 h-4 w-4" />Upload from computer</Button>
                  <Button variant="outline" onClick={startPhoneUpload} data-testid="upload-from-phone" className="rounded-lg"><Smartphone className="mr-1.5 h-4 w-4" />Upload from phone (QR)</Button>
                  <span className="ml-auto self-center text-sm text-[#8B9698]" data-testid="voice-upload-count">{images.length} attached</span>
                </div>
                {uploadSession && (
                  <div className="mt-4 flex flex-col items-center gap-3 rounded-xl border border-dashed border-[#00A85A]/40 bg-[#00A85A]/5 p-4 sm:flex-row" data-testid="qr-panel">
                    <div className="shrink-0 rounded-lg bg-white p-2"><QRCodeSVG value={`${window.location.origin}/m/upload/${uploadSession.token}`} size={104} fgColor="#0B1512" /></div>
                    <div><div className="flex items-center gap-1.5 font-bold text-[#0B1512] dark:text-white"><QrCode className="h-4 w-4 text-[#00A85A]" />Scan with your phone camera</div>
                      <p className="mt-1 text-sm text-[#8B9698]">Photos appear here automatically. Link valid for 6 hours.</p></div>
                  </div>
                )}
                {images.length > 0 && <div className="mt-4"><ImagingReview images={images} onUpdate={setImages} /></div>}
              </div>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Button variant="ghost" onClick={() => setPhase("check")} className="rounded-full" data-testid="voice-upload-back"><ChevronLeft className="mr-1 h-4 w-4" />Back</Button>
                {images.length === 0 && <Button variant="outline" onClick={toReview} className="h-11 rounded-full px-6" data-testid="voice-upload-skip">Skip</Button>}
                <Button onClick={toReview} className="h-11 rounded-full bg-[#00A85A] px-6 text-white hover:bg-[#008F4D]" data-testid="voice-to-review">Continue to review<ArrowRight className="ml-1.5 h-4 w-4" /></Button>
              </div>
            </motion.div>
          )}

          {/* ── SUMMARY ───────────────────────────────────────────────────── */}
          {phase === "summary" && c && (
            <motion.div key="summary" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#00A85A]/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#00A85A]"><Check className="h-3.5 w-3.5" />Structured</div>
              <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-[#0B1512] dark:text-white sm:text-4xl">Here's what CONTAHA understood</h2>
              <p className="mt-2 text-sm text-[#8B9698]">AI assists. You decide. Everything here is editable.</p>
              <div className="mx-auto mt-6 max-w-md rounded-2xl border border-black/5 bg-white/70 p-4 backdrop-blur-sm dark:border-white/10 dark:bg-white/[0.04]">
                <ChecklistPanel topics={topics} c={c} images={images} />
              </div>

              {/* duplicate patient guard */}
              {dup && (
                <div className="mx-auto mt-6 max-w-xl rounded-2xl border border-amber-300 bg-amber-50 p-4 text-left dark:border-amber-500/30 dark:bg-amber-500/10">
                  <div className="flex items-center gap-2 text-sm font-bold text-amber-700 dark:text-amber-300"><AlertTriangle className="h-4 w-4" />Existing patient found</div>
                  <p className="mt-1 text-xs text-amber-700/90 dark:text-amber-200/80">A patient named <b>{dup.name}</b> ({dup.patient_id}) already exists. How would you like to proceed?</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => resolveDup(true)} data-testid="dup-continue" className="rounded-lg bg-[#00A85A] text-white hover:bg-[#008F4D]">Continue with this patient</Button>
                    <Button size="sm" variant="outline" onClick={() => resolveDup(false)} data-testid="dup-create" className="rounded-lg">Create new patient</Button>
                  </div>
                </div>
              )}

              <div className="mx-auto mt-8 grid max-w-2xl grid-cols-1 gap-3 text-left sm:grid-cols-2">
                <StructCard label="Patient" delay={0.05}>
                  <input data-testid="edit-patient-name" value={c.patient_name || ""} onChange={(e) => setField({ patient_name: e.target.value })} onBlur={() => persist({})} className="w-full border-b border-transparent bg-transparent text-sm font-bold text-[#0B1512] outline-none focus:border-[#00A85A] dark:text-white" />
                  <div className="text-xs text-[#8B9698]">
                    {[c.extracted_patient?.age && `${c.extracted_patient.age} yrs`, c.extracted_patient?.gender].filter(Boolean).join(" · ") || "Details from voice"}
                  </div>
                </StructCard>
                <StructCard label="Chief Complaint" delay={0.1}>
                  <input data-testid="edit-chief-complaint" value={c.chief_complaint || ""} placeholder="Not provided" onChange={(e) => setField({ chief_complaint: e.target.value })} onBlur={() => persist({})} className="w-full border-b border-transparent bg-transparent text-sm text-[#0B1512] outline-none placeholder:text-[#8B9698] focus:border-[#00A85A] dark:text-white" />
                </StructCard>
                <StructCard label="Clinical Findings" delay={0.15}>
                  {c.tooth_findings?.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {c.tooth_findings.map((f) => (
                        <span key={f.fdi} className="inline-flex items-center gap-1 rounded-md bg-[#00A85A]/10 px-2 py-0.5 text-[11px] font-semibold text-[#008F4D] dark:text-[#3FD98A]">
                          {f.fdi} — {STATUS_HINT[f.status] || f.status || "Finding"}
                          {f.confidence != null && f.confidence < 80 && <AlertTriangle className="h-3 w-3 text-amber-500" />}
                          <button data-testid={`remove-tooth-${f.fdi}`} onClick={() => removeTooth(f.fdi)} className="ml-0.5 text-[#008F4D]/60 hover:text-red-500"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  ) : <span className="text-xs text-[#8B9698]">None captured</span>}
                </StructCard>
                <StructCard label="Soft Tissue" delay={0.2}>
                  <input data-testid="edit-soft-tissue" value={c.soft_tissue || ""} placeholder="Not assessed" onChange={(e) => setField({ soft_tissue: e.target.value })} onBlur={() => persist({})} className="w-full border-b border-transparent bg-transparent text-sm capitalize text-[#0B1512] outline-none placeholder:normal-case placeholder:text-[#8B9698] focus:border-[#00A85A] dark:text-white" />
                </StructCard>
                {c.sites?.length > 0 && (
                  <StructCard label="Site Measurements" delay={0.25}>
                    <div className="space-y-1">
                      {c.sites.map((s, i) => (
                        <div key={i} className="text-xs text-[#0B1512] dark:text-white">
                          <b>Site {s.site || "?"}</b> {[s.md && `MD ${s.md}`, s.bl && `BL ${s.bl}`, s.vertical && `V ${s.vertical}`].filter(Boolean).join(" · ") || "—"}
                        </div>
                      ))}
                    </div>
                  </StructCard>
                )}
                <StructCard label="Edentulous Status" delay={0.3}>
                  <div className="text-sm capitalize text-[#0B1512] dark:text-white">{c.arch_status?.replace(/_/g, " ") || <span className="text-[#8B9698] normal-case">Not assessed</span>}</div>
                </StructCard>
              </div>

              {/* low-confidence confirmations */}
              {(c.tooth_findings || []).filter((f) => f.confidence != null && f.confidence < 80).map((f) => (
                <div key={f.fdi} className="mx-auto mt-3 flex max-w-2xl items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-2.5 dark:border-amber-500/30 dark:bg-amber-500/10">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <span className="flex-1 text-left text-xs text-amber-700 dark:text-amber-300">Did you say tooth <b>{f.fdi}</b> is <b>{f.status}</b>?</span>
                  <Button size="sm" variant="outline" className="h-7 rounded-md" onClick={() => confirmTooth(f.fdi)} data-testid={`confirm-tooth-${f.fdi}`}><Check className="h-3 w-3" /></Button>
                </div>
              ))}

              <div className="mx-auto mt-8 max-w-2xl rounded-2xl border border-black/5 bg-white/70 p-5 text-left backdrop-blur-sm dark:border-white/10 dark:bg-white/[0.04]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><ImageIcon className="h-4 w-4 text-[#00A85A]" /><span className="text-sm font-bold text-[#0B1512] dark:text-white">Documents & Imaging</span></div>
                  <span className="text-xs text-[#8B9698]">{images.length} attached</span>
                </div>
                <p className="mt-1 text-xs text-[#8B9698]">Add OPG, IOPA, CBCT or clinical photos. Upload here, or scan the QR to add from your phone.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} data-testid="imaging-file-input" />
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="upload-from-computer" className="rounded-lg"><Upload className="mr-1.5 h-4 w-4" />Upload from computer</Button>
                  <Button variant="outline" size="sm" onClick={startPhoneUpload} data-testid="upload-from-phone" className="rounded-lg"><Smartphone className="mr-1.5 h-4 w-4" />Upload from phone</Button>
                </div>
                {uploadSession && (
                  <div className="mt-4 flex flex-col items-center gap-3 rounded-xl border border-dashed border-[#00A85A]/40 bg-[#00A85A]/5 p-4 sm:flex-row sm:gap-4" data-testid="qr-panel">
                    <div className="rounded-lg bg-white p-2 shrink-0"><QRCodeSVG value={`${window.location.origin}/m/upload/${uploadSession.token}`} size={104} fgColor="#0B1512" /></div>
                    <div className="text-center sm:text-left">
                      <div className="flex items-center justify-center gap-1.5 text-sm font-bold text-[#0B1512] dark:text-white sm:justify-start"><QrCode className="h-4 w-4 text-[#00A85A]" />Scan with your phone camera</div>
                      <p className="mt-1 text-xs text-[#8B9698]">Opens a secure upload page. Photos appear here automatically. Link valid for 6 hours.</p>
                      <button data-testid="copy-upload-link" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/m/upload/${uploadSession.token}`); toast.success("Upload link copied"); }} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A85A] hover:underline"><Copy className="h-3.5 w-3.5" />Copy link instead</button>
                    </div>
                  </div>
                )}
                {images.length > 0 && (
                  <div className="mt-4 text-left"><ImagingReview images={images} onUpdate={setImages} /></div>
                )}
                {images.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-5" data-testid="imaging-grid">
                    {images.map((im) => (
                      <div key={im.id} className="group relative aspect-square overflow-hidden rounded-lg border border-black/5 bg-[#F4FAF6] dark:border-white/10 dark:bg-white/5">
                        {im.content_type?.startsWith("image/")
                          ? <img src={`${API}/files/${im.storage_path}?auth=${token}`} alt={im.image_type} className="h-full w-full object-cover" />
                          : <div className="flex h-full w-full items-center justify-center"><FileUp className="h-6 w-6 text-[#8B9698]" /></div>}
                        {!im.quality_ok && <span className="absolute right-1 top-1 rounded bg-amber-500 px-1 py-0.5 text-[8px] font-bold text-white">LOW</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {manualFields.length > 0 && (
                <div className="mx-auto mt-6 max-w-2xl rounded-2xl border border-amber-200 bg-amber-50/70 p-5 text-left dark:border-amber-500/30 dark:bg-amber-500/10" data-testid="manual-confirm-panel">
                  <div className="flex items-center gap-2"><Keyboard className="h-4 w-4 text-amber-600 dark:text-amber-400" /><span className="text-sm font-bold text-amber-800 dark:text-amber-300">A few things to confirm manually</span></div>
                  <p className="mt-1 text-xs text-amber-700/90 dark:text-amber-200/80">These fields can't be captured by voice. Please select or enter them before analysis.</p>
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {manualFields.map((q) => (
                      <div key={q.id} className={q.type === "long_text" ? "sm:col-span-2" : ""}>
                        <label className="text-xs font-semibold text-[#0B1512] dark:text-white">{q.label}{q.required && <span className="text-red-500"> *</span>}</label>
                        <div className="mt-1.5">{manualField(q)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mx-auto mt-9 flex max-w-xl flex-col items-center gap-3">
                <Button data-testid="analyze-case-btn" onClick={generatePlan} disabled={generating || !hasData || !!dup} className="w-full rounded-full bg-[#00A85A] py-4 text-[15px] font-bold text-white hover:bg-[#008F4D]">
                  {generating ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Analyzing case…</> : <><Sparkles className="mr-2 h-5 w-5" />Analyze Case & Generate Treatment Plan</>}
                </Button>
                <div className="flex items-center gap-4 text-xs">
                  <button data-testid="open-full-consultation" onClick={() => navigate(`/consultation/${c.id}`)} className="inline-flex items-center gap-1.5 font-semibold text-[#00A85A] hover:underline">
                    <Pencil className="h-3.5 w-3.5" />Open full consultation to edit
                  </button>
                  <button onClick={() => beginConsultation(patientCtx)} className="inline-flex items-center gap-1.5 font-semibold text-[#8B9698] hover:text-[#0B1512] dark:hover:text-white">
                    <Mic className="h-3.5 w-3.5" />Add more voice
                  </button>
                </div>
                {!hasData && <p className="text-[11px] text-[#8B9698]">Add findings or record again to enable analysis.</p>}
              </div>
            </motion.div>
          )}

          {/* ── MIC ERROR ─────────────────────────────────────────────────── */}
          {phase === "mic_error" && (
            <motion.div key="mic_error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-amber-500/10"><MicOff className="h-9 w-9 text-amber-500" /></div>
              <h2 className="mt-5 text-2xl font-extrabold tracking-tight text-[#0B1512] dark:text-white sm:text-3xl">Microphone unavailable</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-[#8B9698]">We couldn't access your microphone. Check browser permissions, or continue with manual entry — the same structured workflow applies.</p>
              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <Button data-testid="mic-retry" onClick={retryMic} className="h-11 rounded-full bg-[#00A85A] px-6 text-white hover:bg-[#008F4D]"><Mic className="mr-1.5 h-4 w-4" />Try microphone again</Button>
                <Button data-testid="continue-manually" variant="outline" onClick={() => navigate(c?.id ? `/consultation/${c.id}/wizard` : "/consultation/new")} className="h-11 rounded-full px-6">Continue manually<ArrowRight className="ml-1.5 h-4 w-4" /></Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Patient selection modal ─────────────────────────────────────────── */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="patient-modal">
          {modalStep === "choose" && (
            <>
              <DialogHeader><DialogTitle>Who is this consultation for?</DialogTitle><DialogDescription>Start a new patient from voice, or attach to an existing record.</DialogDescription></DialogHeader>
              <div className="mt-2 grid grid-cols-1 gap-3">
                <button data-testid="modal-new-patient" onClick={() => beginConsultation(null)} className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] p-4 text-left transition-colors hover:border-[#00A85A]/50 dark:border-[#223139]">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E8F7F0] dark:bg-[#00A85A]/15"><UserPlus className="h-5 w-5 text-[#00A85A]" /></span>
                  <span><span className="block text-sm font-bold text-[#101516] dark:text-white">New Patient</span><span className="block text-xs text-[#8B9698]">Just start speaking — details captured from voice.</span></span>
                </button>
                <button data-testid="modal-existing-patient" onClick={() => setModalStep("search")} className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] p-4 text-left transition-colors hover:border-[#00A85A]/50 dark:border-[#223139]">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E8F7F0] dark:bg-[#00A85A]/15"><Search className="h-5 w-5 text-[#00A85A]" /></span>
                  <span><span className="block text-sm font-bold text-[#101516] dark:text-white">Existing Patient</span><span className="block text-xs text-[#8B9698]">Attach to an existing patient record.</span></span>
                </button>
              </div>
            </>
          )}
          {modalStep === "search" && (
            <>
              <DialogHeader><DialogTitle>Find patient</DialogTitle><DialogDescription>Search by name, mobile number, email or patient ID.</DialogDescription></DialogHeader>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8B9698]" />
                <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, mobile, email or patient ID" className="h-11 pl-9" data-testid="modal-search-input" />
              </div>
              <div className="mt-2 max-h-72 space-y-2 overflow-y-auto thin-scroll">
                {results.map((p) => (
                  <button key={p.id} data-testid={`modal-result-${p.id}`} onClick={() => { setCandidate(p); setModalStep("context"); }} className="flex w-full items-center gap-3 rounded-xl border border-[#E2E8F0] p-3 text-left hover:border-[#00A85A]/50 dark:border-[#223139]">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E8F7F0] text-xs font-bold text-[#008F4D] dark:bg-[#00A85A]/15 dark:text-[#3FD98A]">{initials(p.name)}</span>
                    <span className="flex-1"><span className="block text-sm font-semibold text-[#101516] dark:text-white">{p.name}</span><span className="block text-xs text-[#8B9698]">{p.patient_id} · {p.age || "—"}y · {p.phone}</span></span>
                    <ArrowRight className="h-4 w-4 text-[#8B9698]" />
                  </button>
                ))}
                {q && results.length === 0 && <p className="py-4 text-center text-sm text-[#8B9698]">No patients found. <button onClick={() => beginConsultation(null)} className="font-semibold text-[#00A85A]">Create new patient</button></p>}
              </div>
              <button onClick={() => setModalStep("choose")} className="mt-1 flex items-center gap-1 text-xs font-semibold text-[#8B9698] hover:text-[#00A85A]"><ChevronLeft className="h-3.5 w-3.5" />Back</button>
            </>
          )}
          {modalStep === "context" && candidate && (
            <>
              <DialogHeader><DialogTitle>Patient context</DialogTitle><DialogDescription>Confirm the patient, then start the voice consultation.</DialogDescription></DialogHeader>
              <div className="mt-1 rounded-xl border border-[#E2E8F0] bg-[#F4FBF7] p-4 dark:border-[#223139] dark:bg-[#0E1A21]">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#00A85A] text-sm font-bold text-white">{initials(candidate.name)}</span>
                  <div><div className="text-sm font-bold text-[#101516] dark:text-white">{candidate.name}</div><div className="text-xs text-[#8B9698]">{candidate.patient_id} · {candidate.age || "—"}y · {candidate.gender || "—"}</div></div>
                </div>
                {candidate.chief_complaint && <p className="mt-3 text-xs text-[#687173] dark:text-[#8B9698]"><b>Last complaint:</b> {candidate.chief_complaint}</p>}
              </div>
              <Button data-testid="modal-start-consultation" onClick={() => beginConsultation(candidate)} className="mt-3 h-11 w-full rounded-lg bg-[#00A85A] font-semibold text-white hover:bg-[#008F4D]"><Mic className="mr-2 h-4 w-4" />Start Consultation</Button>
              <button onClick={() => setModalStep("search")} className="flex items-center gap-1 text-xs font-semibold text-[#8B9698] hover:text-[#00A85A]"><ChevronLeft className="h-3.5 w-3.5" />Back to search</button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
